import { useCallback, useState } from "react";

import AccordionWrapper from "react-faq-component";

import tradeDeskLogo from "@assets/tradedesk-logo.svg";
import { useUser } from "@components/user-context";
import { LearnMoreTooltip } from "@features/sales-v3/top-level-filters/components/learn-more-tooltip";
import { ConnectionType } from "@north-beam/nb-client-side";
import { ConnectionCard } from "@pages/settings/connection-card";
import {
  type LoginComponentChildrenProps,
  type LoginComponentProps,
} from "@pages/settings/connection-status-manager";
import { PrimaryButton } from "@shared/buttons";
import { Switch } from "@shared/switch";
import TextBoxWithCopyButton from "@shared/text-box-with-copy-button";

import { SupportedChannelsSelector } from "./shared/supported-channels-selector";

const type = ConnectionType.integration_trade_desk;

const CLICK_UTMS =
  // eslint-disable-next-line max-len
  "nb_platform=tradedesk&nb_campaignid=%%TTD_CAMPAIGNID%%&nb_adsetid=%%TTD_ADGROUPID%%&nb_creativeid=%%TTD_CREATIVEID%%&nb_devicefamily=%%TTD_DEVICEOSFAMILY%%&nb_devicetype=%%TTD_DEVICETYPE%%&nbt_site=%%TTD_SITE%%&nbt_vendor=%%TTD_SUPPLYVENDOR_INT%%";

export function TradeDeskConnectionCard() {
  const successfulConnectionExtraText = useCallback(
    () => (
      <AccordionWrapper
        data={{
          rows: [
            {
              title: "Open Click UTMs",
              content: (
                <>
                  <p className="card-text">
                    Click UTMs
                    <TextBoxWithCopyButton
                      className="pl-8 max-w-xl"
                      text={CLICK_UTMS}
                      textCopiedNotificationMessage="UTMs copied!"
                    />
                  </p>
                </>
              ),
            },
          ],
        }}
        styles={{
          bgColor: "white",
          rowContentTextSize: "16px",
          rowContentPaddingTop: "10px",
          rowContentPaddingBottom: "10px",
          rowContentPaddingRight: "20px",
          arrowColor: "black",
        }}
        config={{ tabFocus: true }}
      />
    ),
    [],
  );
  return (
    <ConnectionCard
      type={type}
      connectionTypeName="TradeDesk"
      connectionCallToAction="Integrate your TradeDesk account"
      logoUrl={tradeDeskLogo}
      logoAltText="TradeDesk Logo"
      successfulConnectionExtraText={successfulConnectionExtraText}
      knowledgeBaseLink="https://docs.northbeam.io/docs/tracking-for-tradedesk"
    >
      {({
        upsertConnection,
        isLoading,
        accounts,
      }: LoginComponentChildrenProps) => (
        <TradeDeskLoginComponent
          upsertConnection={upsertConnection}
          isLoading={isLoading}
          accounts={accounts}
        />
      )}
    </ConnectionCard>
  );
}

function TradeDeskLoginComponent({
  upsertConnection,
  isLoading,
}: Omit<LoginComponentProps, "setStatus" | "integrationDetails">) {
  const [hasConfiguredClickUTMs, setHasConfiguredClickUTMs] = useState(false);
  const [hasConfiguredScheduledReport, setHasConfiguredScheduledReport] =
    useState(false);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);

  const { user } = useUser();

  const onConnectClicked = async () => {
    await upsertConnection({});
  };

  const availableChannels = [
    {
      value: "audio",
      label: "Audio",
      showWarningOnSelected: true,
    },
    {
      value: "digital-out-of-home",
      label: "Digital Out Of Home",
      showWarningOnSelected: true,
    },
    {
      value: "display",
      label: "Display",
    },
    {
      value: "native-display",
      label: "Native Display",
    },
    {
      value: "native-video",
      label: "Native Video",
    },
    {
      value: "tv",
      label: "TV",
      showWarningOnSelected: true,
    },
    {
      value: "video",
      label: "Video",
    },
  ];

  const selectedChannelsWithWarning = availableChannels.filter(
    (channel) =>
      channel.showWarningOnSelected && selectedChannels.includes(channel.value),
  );

  const warningMessage =
    selectedChannelsWithWarning.length > 1
      ? "Attribution is unsupported for these channels"
      : "Attribution is unsupported for this channel";
  return (
    <div className="mb-3">
      <div className="flex flex-col gap-y-5">
        <div>
          <div>TradeDesk Channels</div>
          <SupportedChannelsSelector
            className="max-w-xl"
            selectedChannels={selectedChannels}
            initialOptions={availableChannels}
            onChange={setSelectedChannels}
            WarningMessageContainer={({ children, className }) => (
              <LearnMoreTooltip
                title="TradeDesk Channels"
                content={warningMessage}
                link="https://docs.northbeam.io/docs/tracking-for-tradedesk#important-attribution-notes"
                className={className}
              >
                {children}
              </LearnMoreTooltip>
            )}
            warningMessage={warningMessage}
          />
        </div>
        <div>
          <div className="flex gap-2 items-center mb-2">
            <Switch
              id="trade-desk-click-utms-configuration"
              size="small"
              value={hasConfiguredClickUTMs}
              onChange={setHasConfiguredClickUTMs}
              disabled={isLoading}
            />
            <div>
              I included the UTMs below into my Clickthrough URL:
              <LearnMoreTooltip
                title="Clickthrough UTMs Configuration"
                content="Be sure to include the UTMs in your Clickthrough URL before connecting your TradeDesk account"
                link="https://docs.northbeam.io/docs/tracking-for-tradedesk#step-2-inserting-click-and-impressions-utms"
              />
            </div>
          </div>
          <TextBoxWithCopyButton
            className="pl-8 max-w-xl"
            text={CLICK_UTMS}
            textCopiedNotificationMessage="UTMs copied!"
          />
        </div>
        <div>
          <div className="flex gap-2 items-center mb-2">
            <Switch
              id="trade-desk-scheduled-report"
              size="small"
              value={hasConfiguredScheduledReport}
              onChange={(value) => setHasConfiguredScheduledReport(value)}
              disabled={isLoading}
            />
            <div>
              I created a Scheduled Report to the email below:
              <LearnMoreTooltip
                title="Scheduled Report"
                content="Be sure to create a Scheduled Report before connecting your TradeDesk account"
                link="https://docs.northbeam.io/docs/tracking-for-tradedesk#step-3-creating-a-scheduled-report"
              />
            </div>
          </div>
          <TextBoxWithCopyButton
            className="pl-8 max-w-xl"
            text={`${user.displayUserId}zzznbzzztradedesk@robot.northbeam.io`}
            textCopiedNotificationMessage="Email copied!"
          />
        </div>
        <div>
          <PrimaryButton
            loading={isLoading}
            disabled={
              !hasConfiguredClickUTMs ||
              !hasConfiguredScheduledReport ||
              !selectedChannels.length
            }
            onClick={onConnectClicked}
          >
            {isLoading ? "Please Wait..." : "CONNECT"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
